import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { IJobOrder, IJobOrderDataState } from 'app/models';
import { JobOrderService } from 'app/services/job/job.order.service';
import { MessageService } from 'app/services/message/message.service';
import {
  ConfirmDialogModel,
  ConfirmDialogComponent,
} from '../confirm-dialog/confirm-dialog.component';
import { JobOrderDataStatesFilters } from '../job-order-data-state-filter/job-order-data-state-filter.component';
import {
  JobOrderDataStateElement,
  JobOrderDataStateElementSelectioState,
} from '../job-order-data-state-list/job-order-data-state-list.component';

export enum JobOrdersComponentState {
  IDLE = 'IDLE',
  UPDATE = 'UPDATE',
  NEW = 'NEW',
}
@Component({
  selector: 'app-job-orders-component',
  templateUrl: './job-orders.component.html',
  styleUrls: ['./job-orders.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class JobOrdersComponent implements OnInit {
  jobOrders: IJobOrder[] = [];
  selectedJobOrder?: IJobOrder = undefined;

  dataStateElements: Map<string, JobOrderDataStateElement> = new Map();
  jobOrderDataStates: Map<string, IJobOrderDataState> = new Map();
  shownDataStateElements: Map<string, JobOrderDataStateElement> = new Map();

  pageState = JobOrdersComponentState.UPDATE;

  isJobOrderDataStateLoading = false;

  selectedJobId: number | undefined = undefined;

  constructor(
    private jobOrderService: JobOrderService,
    private messageService: MessageService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.getJobOrders();
  }

  getJobOrders(filters?: {
    code?: string;
    description?: string;
    finalCustomer?: string;
    from?: Date;
    to?: Date;
  }): void {
    this.jobOrderService.getAllJobs(filters).subscribe((jobOrders) => {
      this.jobOrders = [...jobOrders];
      this.pageState = JobOrdersComponentState.IDLE;
    });
  }

  onNewJobOrderClicked() {
    this.selectedJobOrder = undefined;
    this.dataStateElements = new Map();
    this.pageState = JobOrdersComponentState.NEW;
  }

  onJobOrderDataUpdate(jobOrder: IJobOrder) {
    if (jobOrder.id === null) {
      this.jobOrderService.createJobOrder(jobOrder).subscribe(
        () => {
          this.getJobOrders();
          this.pageState = JobOrdersComponentState.IDLE;
        },
        () => {
          this.messageService.showDirectSimpleMessage(
            'Error on saving the job order'
          );
        }
      );
    } else {
      const originalDataStates: JobOrderDataStateElement[] = Array.from(
        this.jobOrderDataStates.values()
      ).map((value) => ({
        ...value,
        isSelected: true,
        selectionState:
          this.dataStateElements.get(value.id)?.selectionState ||
          JobOrderDataStateElementSelectioState.UNCHANGED,
      }));
      this.jobOrderService
        .updateJobOrder(
          jobOrder,
          originalDataStates
            .concat(Array.from(this.dataStateElements.values()))
            .filter(
              (v) =>
                v.selectionState !==
                JobOrderDataStateElementSelectioState.REMOVED
            )
            .map((v) => v.id)
        )
        .subscribe(
          () => {
            this.getJobOrders();
            this.pageState = JobOrdersComponentState.IDLE;
          },
          () => {
            this.messageService.showDirectSimpleMessage(
              ` Error on creating the job order `
            );
          }
        );
    }
  }

  onJobOrderUpdateCancel() {
    this.pageState = JobOrdersComponentState.IDLE;
    this.selectedJobOrder = undefined;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onJobOrderFilterChange(event: any) {
    this.getJobOrders({
      finalCustomer: event.jobCustomer,
      code: event.jobName,
      from: event.fromDate,
      to: event.toDate,
    });
  }

  onJobOrderUpdateActionClick(id: number) {
    this.selectedJobOrder =
      this.jobOrders.find((j) => j.id === id) ?? undefined;

    this.refreshSelectedJobOrderDataStates(() => {
      this.pageState = JobOrdersComponentState.UPDATE;
    });
  }

  onJobOrderDeleteActionClick(jobId: number) {
    const message = `Are you sure you want delete this job order?`;
    const dialogData = new ConfirmDialogModel('Confirm Delete Action', message);

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((dialogResult) => {
      if (dialogResult) {
        this.jobOrderService.deleteJobOrder(jobId).subscribe(() => {
          this.getJobOrders();
        });
      }
    });
  }

  onJobOrderDetailActionClick(jobOrderId: number) {
    this.selectedJobId = jobOrderId;
  }

  onJobOrderDataStateFilterChange(event: JobOrderDataStatesFilters) {
    if (!this.selectedJobOrder?.id) {
      return;
    }
    this.isJobOrderDataStateLoading = true;
    this.jobOrderService
      .getJobOrderDataStates({
        description: event.fileName,
        from: event.fromDate,
        to: event.toDate,
      })
      .subscribe(
        (dataStates) => {
          if (this.selectedJobOrder) {
            this.shownDataStateElements.clear();
            this.isJobOrderDataStateLoading = false;
            dataStates.forEach((value) => {
              const selectionState = this.dataStateElements.has(value.id)
                ? this.dataStateElements.get(value.id)?.selectionState
                : JobOrderDataStateElementSelectioState.UNCHANGED;
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              const isCurrentlySelected = value.jobOrderIds.includes(this.selectedJobOrder!.id);
              this.shownDataStateElements.set(value.id, {
                ...value,
                isSelected:
                  isCurrentlySelected ||
                  selectionState ===
                  JobOrderDataStateElementSelectioState.ADDED,
                selectionState:
                  selectionState ||
                  JobOrderDataStateElementSelectioState.UNCHANGED,
              });
            });
          }
        },
        () => {
          this.isJobOrderDataStateLoading = false;
          this.messageService.showDirectSimpleMessage(
            ` Error on loading job order data states `
          );
        }
      );
  }

  onDataStateCheckChanged(dataState: JobOrderDataStateElement) {
    let ds = {
      ...dataState,
      selectionState: JobOrderDataStateElementSelectioState.UNCHANGED,
    };
    const originalDataState = this.jobOrderDataStates.get(dataState.id);
    switch (dataState.selectionState) {
      case JobOrderDataStateElementSelectioState.UNCHANGED: {
        ds = {
          ...ds,
          isSelected: originalDataState ? !originalDataState : true,
          selectionState: originalDataState
            ? JobOrderDataStateElementSelectioState.REMOVED
            : JobOrderDataStateElementSelectioState.ADDED,
        };
        break;
      }
      case JobOrderDataStateElementSelectioState.ADDED: {
        ds = {
          ...ds,
          isSelected: false,
        };
        break;
      }
      case JobOrderDataStateElementSelectioState.REMOVED: {
        if (originalDataState) {
          ds = {
            ...ds,
            isSelected: true,
          };
        }
        break;
      }
    }
    this.dataStateElements.set(dataState.id, ds);
    const m = new Map(this.shownDataStateElements);
    m.set(dataState.id, ds);
    this.shownDataStateElements = m;
  }

  onBackClick() {
    this.selectedJobId = undefined;
  }

  onViewAllSelectedDataStates() {
    const unchangedStates = new Map<string, JobOrderDataStateElement>();
    this.jobOrderDataStates.forEach((value, key) => {
      unchangedStates.set(key, {
        ...value,
        isSelected: true,
        selectionState: JobOrderDataStateElementSelectioState.UNCHANGED,
      });
    });
    this.shownDataStateElements = new Map([
      ...unchangedStates,
      ...this.dataStateElements.entries(),
    ]);
  }

  private refreshSelectedJobOrderDataStates(onSuccess: () => void = () => {}) {
    this.dataStateElements = new Map();
    this.shownDataStateElements = new Map();
    this.jobOrderDataStates = new Map();
    if (this.selectedJobOrder) {
      this.isJobOrderDataStateLoading = true;
      this.jobOrderService
        .getDataStatesForJobOrder(this.selectedJobOrder.id)
        .subscribe(
          (dataStates) => {
            if (this.selectedJobOrder) {
              this.isJobOrderDataStateLoading = false;
              this.jobOrderDataStates = new Map(
                dataStates.map((value) => [value.id, value])
              );
              this.jobOrderDataStates.forEach((value, id) => {
                this.shownDataStateElements.set(id, {
                  ...value,
                  isSelected: true,
                  selectionState:
                    JobOrderDataStateElementSelectioState.UNCHANGED,
                });
              });
              onSuccess();
            }
          },
          () => {
            this.isJobOrderDataStateLoading = false;
            this.messageService.showDirectSimpleMessage(
              `Error on loading job order data states `
            );
          }
        );
    }
  }
}
